import { api } from "aldoo-ra/API";

// Create API client for product-availability service
const availabilityAPI = api("product-availability");

/**
 * PRODUCT AVAILABILITY ACTIONS
 */

/**
 * Check availability of a specific product
 * @param {string} pid - Product ID to check
 * @returns {Promise<Object>} - API response with availability status
 * 
 * Response format:
 * {
 *   result: true,
 *   pid: "product-pid",
 *   available: true,
 *   in_stock: true
 * }
 */
export const CheckProductAvailability = async (pid) => {
    try {
        if (!pid) {
            throw new Error('Product ID is required');
        }

        const response = await availabilityAPI({
            check: { pid }
        });

        return response;
    } catch (error) {
        console.error('Error checking product availability:', error);
        throw error;
    }
};

/**
 * Get list of all available resolvers
 * @returns {Promise<Object>} - API response with resolvers list
 * 
 * Response format:
 * {
 *   result: true,
 *   resolvers: [
 *     {
 *       id: "in-stock",
 *       name: "Always In Stock",
 *       description: "Product is always available (typical for software licenses)"
 *     },
 *     {
 *       id: "license-keys",
 *       name: "License Keys Available",
 *       description: "Check if there are free license keys in associated packs"
 *     }
 *   ],
 *   count: 2
 * }
 */
export const GetAvailabilityResolvers = async () => {
    try {
        const response = await availabilityAPI({
            resolvers: true
        });

        return response;
    } catch (error) {
        console.error('Error fetching availability resolvers:', error);
        throw error;
    }
};

/**
 * BATCH AVAILABILITY CHECKING
 */

/**
 * Check availability for multiple products at once
 * @param {Array<string>} pids - Array of product IDs to check
 * @returns {Promise<Object>} - Object with availability status for each product
 * 
 * Response format:
 * {
 *   "product-1": { available: true, pid: "product-1" },
 *   "product-2": { available: false, pid: "product-2" }
 * }
 */
export const CheckMultipleProductAvailability = async (pids) => {
    try {
        if (!Array.isArray(pids) || pids.length === 0) {
            throw new Error('Product IDs array is required');
        }

        // Execute all checks in parallel
        const checks = pids.map(pid => CheckProductAvailability(pid));
        const results = await Promise.allSettled(checks);

        // Process results
        const availabilityMap = {};
        results.forEach((result, index) => {
            const pid = pids[index];
            if (result.status === 'fulfilled' && result.value.result) {
                availabilityMap[pid] = {
                    available: result.value.available,
                    in_stock: result.value.in_stock,
                    pid: pid
                };
            } else {
                // Default to unavailable if check failed
                availabilityMap[pid] = {
                    available: false,
                    in_stock: false,
                    pid: pid,
                    error: result.reason?.message || 'Check failed'
                };
            }
        });

        return {
            result: true,
            data: availabilityMap,
            checkedCount: pids.length
        };
    } catch (error) {
        console.error('Error checking multiple product availability:', error);
        throw error;
    }
};

/**
 * HELPER FUNCTIONS
 */

// Cache for availability resolvers
let cachedResolvers = null;
let loadingResolversPromise = null;
let resolversInitialized = false;

/**
 * Get all resolver options for frontend dropdowns
 * @returns {Promise<Array>} - Array of resolver options for UI components
 * 
 * Returns format suitable for select dropdowns:
 * [
 *   { value: "in-stock", label: "Always In Stock", description: "..." },
 *   { value: "license-keys", label: "License Keys Available", description: "..." }
 * ]
 */
export const GetResolverOptions = async () => {
    try {
        // Return cached options if available
        if (cachedResolvers) {
            return cachedResolvers;
        }

        // If already loading, return the same promise
        if (loadingResolversPromise) {
            return loadingResolversPromise;
        }

        // Create loading promise
        loadingResolversPromise = fetchResolverOptions();
        
        try {
            const options = await loadingResolversPromise;
            cachedResolvers = options;
            return options;
        } catch (error) {
            loadingResolversPromise = null;
            throw error;
        } finally {
            loadingResolversPromise = null;
        }
    } catch (error) {
        console.error('Error getting resolver options:', error);
        // Return basic options as fallback
        return getDefaultResolverOptions();
    }
};

/**
 * Fetch resolver options from API
 * @returns {Promise<Array>} - Array of resolver options
 */
const fetchResolverOptions = async () => {
    const response = await GetAvailabilityResolvers();

    if (response.result && response.resolvers) {
        const options = response.resolvers.map(resolver => ({
            value: resolver.id,
            label: resolver.name,
            description: resolver.description,
            id: resolver.id
        }));
        return options;
    }

    return getDefaultResolverOptions();
};

/**
 * Get default resolver options
 * @returns {Array} - Default resolver options
 */
const getDefaultResolverOptions = () => [
    {
        value: 'in-stock',
        label: 'Always In Stock',
        description: 'Product is always available (typical for software licenses)'
    },
    {
        value: 'out-of-stock',
        label: 'Out of Stock', 
        description: 'Product is manually marked as unavailable'
    }
];

/**
 * Get resolver options synchronously for CMS fields
 * @returns {Array} - Array of resolver options
 */
export const GetResolverOptionsForCMS = () => {
    // If not cached, trigger a fetch (but don't wait for it)
    if (!cachedResolvers) {
        GetResolverOptions().catch(() => {
            // Silently handle errors
        });
    }
    
    return cachedResolvers || getDefaultResolverOptions();
};

/**
 * Get resolver by ID with full details
 * @param {string} resolverId - Resolver ID to find
 * @returns {Promise<Object|null>} - Resolver details or null if not found
 */
export const GetResolverById = async (resolverId) => {
    try {
        const response = await GetAvailabilityResolvers();

        if (response.result && response.resolvers) {
            return response.resolvers.find(resolver => resolver.id === resolverId) || null;
        }

        return null;
    } catch (error) {
        console.error('Error getting resolver by ID:', error);
        return null;
    }
};

/**
 * Check if product is available with detailed response
 * @param {string} pid - Product ID to check
 * @returns {Promise<Object>} - Detailed availability information
 * 
 * Enhanced response format:
 * {
 *   pid: "product-pid",
 *   available: true,
 *   in_stock: true,
 *   timestamp: "2024-01-01T12:00:00Z",
 *   cached: false
 * }
 */
export const GetProductAvailabilityDetails = async (pid) => {
    try {
        const response = await CheckProductAvailability(pid);

        if (response.result) {
            return {
                pid: response.pid,
                available: response.available,
                in_stock: response.in_stock,
                timestamp: new Date().toISOString(),
                cached: false
            };
        }

        throw new Error(response.error || 'Failed to check availability');
    } catch (error) {
        console.error('Error getting product availability details:', error);
        // Return unavailable as safe default
        return {
            pid: pid,
            available: false,
            in_stock: false,
            timestamp: new Date().toISOString(),
            error: error.message
        };
    }
};

/**
 * VALIDATION HELPERS
 */

/**
 * Validate resolver configuration for a product
 * @param {Array<string>} resolverIds - Array of resolver IDs
 * @returns {Promise<Object>} - Validation result
 * 
 * Response format:
 * {
 *   valid: true,
 *   resolvers: [...],
 *   issues: []
 * }
 */
export const ValidateResolverConfiguration = async (resolverIds) => {
    try {
        if (!Array.isArray(resolverIds)) {
            return {
                valid: false,
                resolvers: [],
                issues: ['Resolver IDs must be an array']
            };
        }

        const availableResolvers = await GetAvailabilityResolvers();

        if (!availableResolvers.result) {
            return {
                valid: false,
                resolvers: [],
                issues: ['Could not fetch available resolvers']
            };
        }

        const validResolverIds = availableResolvers.resolvers.map(r => r.id);
        const issues = [];
        const validResolvers = [];

        for (const resolverId of resolverIds) {
            if (validResolverIds.includes(resolverId)) {
                const resolver = availableResolvers.resolvers.find(r => r.id === resolverId);
                validResolvers.push(resolver);
            } else {
                issues.push(`Unknown resolver: ${resolverId}`);
            }
        }

        return {
            valid: issues.length === 0,
            resolvers: validResolvers,
            issues: issues
        };
    } catch (error) {
        console.error('Error validating resolver configuration:', error);
        return {
            valid: false,
            resolvers: [],
            issues: ['Validation failed: ' + error.message]
        };
    }
};

/**
 * REAL-TIME MONITORING
 */

/**
 * Monitor product availability with polling
 * @param {string} pid - Product ID to monitor
 * @param {Function} callback - Callback function for status changes
 * @param {number} interval - Polling interval in milliseconds (default: 30000)
 * @returns {Function} - Stop monitoring function
 * 
 * Usage:
 * const stopMonitoring = MonitorProductAvailability('product-1', (status) => {
 *   console.log('Status changed:', status);
 * });
 * 
 * // Later: stopMonitoring();
 */
export const MonitorProductAvailability = (pid, callback, interval = 30000) => {
    let lastStatus = null;
    let isMonitoring = true;

    const checkStatus = async () => {
        if (!isMonitoring) return;

        try {
            const status = await GetProductAvailabilityDetails(pid);

            // Only call callback if status actually changed
            if (lastStatus === null || lastStatus.available !== status.available) {
                callback({
                    ...status,
                    changed: lastStatus !== null,
                    previousStatus: lastStatus
                });
                lastStatus = status;
            }
        } catch (error) {
            callback({
                pid: pid,
                available: false,
                error: error.message,
                timestamp: new Date().toISOString()
            });
        }

        // Schedule next check
        if (isMonitoring) {
            setTimeout(checkStatus, interval);
        }
    };

    // Start monitoring
    checkStatus();

    // Return stop function
    return () => {
        isMonitoring = false;
    };
};

/**
 * CACHING UTILITIES
 */

/**
 * Simple in-memory cache for availability checks
 */
class AvailabilityCache {
    constructor(ttl = 60000) { // 1 minute default TTL
        this.cache = new Map();
        this.ttl = ttl;
    }

    get(pid) {
        const item = this.cache.get(pid);
        if (!item) return null;

        if (Date.now() - item.timestamp > this.ttl) {
            this.cache.delete(pid);
            return null;
        }

        return item.data;
    }

    set(pid, data) {
        this.cache.set(pid, {
            data: data,
            timestamp: Date.now()
        });
    }

    clear() {
        this.cache.clear();
    }

    delete(pid) {
        this.cache.delete(pid);
    }
}

// Global cache instance
const availabilityCache = new AvailabilityCache();

/**
 * Check product availability with caching
 * @param {string} pid - Product ID to check
 * @param {boolean} useCache - Whether to use cache (default: true)
 * @returns {Promise<Object>} - Availability status (may be cached)
 */
export const CheckProductAvailabilityCached = async (pid, useCache = true) => {
    try {
        // Check cache first
        if (useCache) {
            const cached = availabilityCache.get(pid);
            if (cached) {
                return {
                    ...cached,
                    cached: true
                };
            }
        }

        // Fetch fresh data
        const response = await GetProductAvailabilityDetails(pid);

        // Cache the result
        if (useCache && response.available !== undefined) {
            availabilityCache.set(pid, response);
        }

        return response;
    } catch (error) {
        console.error('Error checking cached product availability:', error);
        throw error;
    }
};

/**
 * Clear availability cache for specific product or all products
 * @param {string} [pid] - Product ID to clear (if not provided, clears all)
 */
export const ClearAvailabilityCache = (pid = null) => {
    if (pid) {
        availabilityCache.delete(pid);
    } else {
        availabilityCache.clear();
    }
};

/**
 * TESTING AND DEBUG FUNCTIONS
 */

/**
 * Test product availability API connectivity
 * @returns {Promise<boolean>} - True if API is working
 */
export const TestAvailabilityAPI = async () => {
    try {
        // Test resolvers endpoint
        const resolversResponse = await GetAvailabilityResolvers();
        if (!resolversResponse.result) {
            return false;
        }

        // Test with a non-existent product (should handle gracefully)
        const checkResponse = await CheckProductAvailability('test-product-non-existent');
        // Should return a response even if product doesn't exist

        return true;
    } catch (error) {
        console.error('Availability API test failed:', error);
        return false;
    }
};

/**
 * Get comprehensive availability system status
 * @returns {Promise<Object>} - System status information
 */
export const GetAvailabilitySystemStatus = async () => {
    try {
        const resolversResponse = await GetAvailabilityResolvers();

        return {
            apiWorking: resolversResponse.result === true,
            resolversCount: resolversResponse.resolvers?.length || 0,
            availableResolvers: resolversResponse.resolvers?.map(r => r.id) || [],
            cacheSize: availabilityCache.cache.size,
            timestamp: new Date().toISOString()
        };
    } catch (error) {
        return {
            apiWorking: false,
            error: error.message,
            timestamp: new Date().toISOString()
        };
    }
};

// Export all functions as default object
export default {
    // Core functions
    CheckProductAvailability,
    GetAvailabilityResolvers,
    CheckMultipleProductAvailability,

    // Helper functions
    GetResolverOptions,
    GetResolverById,
    GetProductAvailabilityDetails,
    ValidateResolverConfiguration,

    // Monitoring
    MonitorProductAvailability,

    // Caching
    CheckProductAvailabilityCached,
    ClearAvailabilityCache,

    // Testing
    TestAvailabilityAPI,
    GetAvailabilitySystemStatus
};